import { getRandomId, ROOT_NODE } from '@craftjs/utils';
import {
  type Node,
  type NodeId,
  type NodeTree,
  type SerializedNode,
  useEditor,
} from '@craftjs/core';
import {
  COPY_CRAFT_NODE_PREFIX,
  PASTE_EVENT,
  FORM_NODE_NAME,
  PAGE_NODE_NAME,
  POPUP_NODE_NAME,
  CONTENT_BLOCK_NAME,
} from '@/utils/constants';

import { isFormFieldNode } from '@builder4/shared/components/Form/formRegistry';
import {
  clearFormIdentifiersFromNode,
  clearFormIdentifiersFromSerializedNode,
} from '@/utils/formDuplicationUtils';
import { isContainerNode } from '@/utils/helperFunc';
/**
 * Handle copy and past nodes
 */
const fromEntries = (pairs: [NodeId, SerializedNode | Node][]) => {
  if (Object.fromEntries) {
    return Object.fromEntries(pairs);
  }
  return pairs.reduce(
    (accum, [id, value]) => ({
      ...accum,
      [id]: value,
    }),
    {},
  );
};

export const useCopyPasteHelper = () => {
  const { query, actions } = useEditor();
  // to copy a node
  const getCloneTree = (tree: NodeTree) => {
    const newNodes: Record<string, Node> = {};
    const changeNodeId = (node: Node, newParentId?: NodeId) => {
      const newNodeId = getRandomId();
      const childNodes = node.data.nodes.map((childId: NodeId) =>
        changeNodeId(tree.nodes[childId], newNodeId),
      );
      const linkedNodes = Object.keys(node.data.linkedNodes).reduce((acc, id) => {
        const newLinkedNodeId = changeNodeId(tree.nodes[node.data.linkedNodes[id]], newNodeId);
        return {
          ...acc,
          [id]: newLinkedNodeId,
        };
      }, {});

      const tmpNode = {
        ...node,
        id: newNodeId,
        data: {
          ...node.data,
          parent: newParentId || node.data.parent,
          nodes: childNodes,
          linkedNodes,
        },
      };
      const freshNode = query.parseFreshNode(tmpNode).toNode();
      // Clear unique form and field identifiers
      const clearedNode = clearFormIdentifiersFromNode(freshNode);
      newNodes[newNodeId] = clearedNode;
      return newNodeId;
    };

    const rootNodeId = changeNodeId(tree.nodes[tree.rootNodeId]);
    return {
      rootNodeId,
      nodes: newNodes,
      eventSource: PASTE_EVENT,
    };
  };

  const serializeNode = (id: NodeId) => {
    return query.node(id).toSerializedNode();
  };

  const getSerializedNodeTree = (id: NodeId) => {
    const tree = query.node(id).toNodeTree();
    const nodePairs = Object.keys(tree.nodes).map((id): [NodeId, SerializedNode] => [
      id,
      serializeNode(id),
    ]);
    const serializedNodesJSON = JSON.stringify(fromEntries(nodePairs));
    const saveData = {
      rootNodeId: tree.rootNodeId,
      nodes: serializedNodesJSON,
    };
    return saveData;
  };

  const getSerializedNodeTreeToSave = (id: NodeId) => {
    const tree = query.node(id).toNodeTree();
    const nodePairs = Object.keys(tree.nodes).map((nodeId): [NodeId, SerializedNode] => {
      const serialized = serializeNode(nodeId);
      // Clear unique form and field identifiers when saving sections
      return [nodeId, clearFormIdentifiersFromSerializedNode(serialized)];
    });
    const serializedNodesJSON = fromEntries(nodePairs);
    const saveData = {
      rootNodeId: tree.rootNodeId,
      nodes: serializedNodesJSON,
    };
    return saveData;
  };

  // to save as a template
  const copyNodeTree = (id: NodeId) => {
    const saveData = getSerializedNodeTree(id);
    navigator.clipboard.writeText(COPY_CRAFT_NODE_PREFIX + JSON.stringify(saveData));
  };

  // add templates where you want
  const pasteNodeTree = (targetNodeId: NodeId): Promise<Node[] | false> => {
    return new Promise((resolve) => {
      navigator.clipboard
        .readText()
        .then((text) => {
          if (text.startsWith(COPY_CRAFT_NODE_PREFIX)) {
            const data = JSON.parse(text.slice(COPY_CRAFT_NODE_PREFIX.length));
            const newNodes = JSON.parse(data.nodes);
            const nodePairs = Object.keys(newNodes).map((id): [NodeId, Node] => {
              const nodeId = id;
              return [
                nodeId,
                query.parseSerializedNode(newNodes[id]).toNode((node: Node) => (node.id = nodeId)),
              ];
            });
            const tree: NodeTree = {
              rootNodeId: data.rootNodeId,
              nodes: fromEntries(nodePairs) as Record<string, Node>,
            };
            try {
              const incomingRoot = tree.nodes[tree.rootNodeId];
              const incomingRootName = incomingRoot?.data?.displayName;

              // Helper: find nearest ancestor that is a canvas (droppable area)
              const findNearestCanvasAncestor = (startId: string | null): string | null => {
                let current: string | null = startId;
                while (current) {
                  try {
                    if (query.node(current).isCanvas()) return current;
                  } catch {
                    // ignore broken references
                  }
                  const n = query.node(current).get();
                  current = (n?.data?.parent as string | null) ?? null;
                }
                return null;
              };

              // If pasting a Page node, paste its content into the target's first container
              if (incomingRootName === PAGE_NODE_NAME) {
                const pageChildIds = incomingRoot?.data?.nodes || [];
                if (pageChildIds.length === 0) {
                  resolve(false);
                  return;
                }

                // Helper: collect all descendant node IDs for a subtree
                const collectSubtreeNodes = (
                  nodeId: string,
                  allNodes: Record<string, Node>,
                ): Record<string, Node> => {
                  const result: Record<string, Node> = {};
                  const node = allNodes[nodeId];
                  if (!node) return result;
                  result[nodeId] = node;
                  for (const childId of node.data.nodes || []) {
                    Object.assign(result, collectSubtreeNodes(childId, allNodes));
                  }
                  for (const linkedId of Object.values(node.data.linkedNodes || {})) {
                    Object.assign(result, collectSubtreeNodes(linkedId as string, allNodes));
                  }
                  return result;
                };

                // Landing page can have multiple root containers
                // Copy all children of the landing page's ROOT_NODE to our ROOT_NODE
                const contentNodeIds = pageChildIds; // All children of the source page
                const addedNodes: Node[] = [];
                // Paste content into ROOT_NODE directly
                if (contentNodeIds.length > 0) {
                  let lastPastedNode: Node | null = null;
                  for (let i = 0; i < contentNodeIds.length; i++) {
                    const childId = contentNodeIds[i];
                    const subtreeNodes = collectSubtreeNodes(childId, tree.nodes);
                    const childTree: NodeTree = {
                      rootNodeId: childId,
                      nodes: subtreeNodes,
                    };
                    const newTree = getCloneTree(childTree);
                    // Use merge() for subsequent nodes so all pastes are recorded as a single history event
                    const actionMethod = i === 0 ? actions : actions.history.merge();
                    actionMethod.addNodeTree(newTree, ROOT_NODE);
                    lastPastedNode = query.node(newTree.rootNodeId).get();
                    addedNodes.push(lastPastedNode);
                  }

                  resolve(addedNodes.length > 0 ? addedNodes : false);
                  return;
                }

                resolve(false);
                return;
              } else if (incomingRootName === POPUP_NODE_NAME) {
                // Popups can only exist as root-level nodes
                targetNodeId = ROOT_NODE;
              } else if (incomingRootName === FORM_NODE_NAME) {
                // Block pasting a Form inside any ancestor Form
                let currentId: string | null = targetNodeId as string;
                while (currentId) {
                  const current = query.node(currentId).get();
                  if (!current) break;
                  if (current.data.displayName === FORM_NODE_NAME) {
                    console.warn('Cannot paste a Form inside another Form.');
                    resolve(false);
                    return;
                  }
                  currentId = (current.data.parent as string | null) ?? null;
                }
                // Retarget to a valid canvas (if the clicked layer isn't a canvas)
                const canvasTarget = findNearestCanvasAncestor(targetNodeId as string);
                if (canvasTarget) {
                  targetNodeId = canvasTarget;
                } else {
                  console.warn('No valid canvas found to paste the Form into.');
                  resolve(false);
                  return;
                }
              }

              // If pasting a form field, retarget paste into the nearest ancestor Form; if none, block
              if (isFormFieldNode(incomingRootName)) {
                let formId: string | null = targetNodeId as string;
                while (formId) {
                  const candidate = query.node(formId).get();
                  if (!candidate) break;
                  if (candidate.data.displayName === FORM_NODE_NAME) {
                    targetNodeId = formId; // paste into the Form itself
                    break;
                  }
                  formId = (candidate.data.parent as string | null) ?? null;
                }
                if (!formId) {
                  console.warn(
                    'Form fields (including Submit Button) can only be pasted inside a Form.',
                  );
                  resolve(false);
                  return;
                }
              }

              // Generic case: ensure target is a canvas; if not, use its nearest canvas ancestor
              // ROOT_NODE is a valid target for top-level containers/sections
              if (!isFormFieldNode(incomingRootName) && incomingRootName !== FORM_NODE_NAME) {
                if (targetNodeId === ROOT_NODE) {
                  //pasting a non-container and non-popup node into the root node is not allowed
                  if (
                    incomingRootName !== CONTENT_BLOCK_NAME &&
                    incomingRootName !== POPUP_NODE_NAME
                  ) {
                    //get the last root container
                    const rootNode = query.node(ROOT_NODE).get();
                    if (rootNode) {
                      //need to check if this is a container
                      //reason: May be in old designs users copy pasted non container nodes into the root node
                      const lastNodeIndex = rootNode.data.nodes.findIndex((nodeId) =>
                        isContainerNode(query.node(nodeId).get() as Node),
                      );
                      if (lastNodeIndex !== -1) {
                        targetNodeId = rootNode.data.nodes[lastNodeIndex];
                      }
                    }
                    if (targetNodeId === ROOT_NODE) {
                      //if still root node, then it is not a valid target
                      console.warn('No valid container found to paste into.');
                      resolve(false);
                      return;
                    }
                  }
                }

                if (targetNodeId !== ROOT_NODE) {
                  const canvasTarget = findNearestCanvasAncestor(targetNodeId as string);
                  if (canvasTarget) {
                    targetNodeId = canvasTarget;
                  } else {
                    console.warn('No valid canvas found to paste into.');
                    resolve(false);
                    return;
                  }
                }
              }
            } catch (e) {
              console.warn('Paste validation failed', e);
            }
            const newTree = getCloneTree(tree);
            actions.addNodeTree(newTree, targetNodeId);

            const newNode = query.node(newTree.rootNodeId).get();
            resolve([newNode]);
          } else {
            resolve(false);
          }
        })
        .catch((e) => {
          console.error(e);
          resolve(false);
        });
    });
  };

  return {
    pasteNodeTree,
    copyNodeTree,
    serializeNode,
    getSerializedNodeTree,
    getSerializedNodeTreeToSave,
    fromEntries,
  };
};
